/**
 * Die Rückblende im Essenz-Spiegel — was je Tag zusammenkommt. (25.09.2026,
 * Ticket 2560)
 *
 * Martin: *„Der Essenzspiegel soll wie eine Art Rückblende-Tagebuch
 * funktionieren — pro Tag ein Eintrag, unter dem man sieht, wie der Tag
 * verlaufen ist."* Zum Entwurf: *„Ja, sieht toll aus. Du kannst loslegen.“*
 *
 * Die Teile gab es alle schon, nur an vier Stellen:
 *   - Verfassung: `mood_days.valenz` (-2 … +2, Nachtlauf) → fünf Punkte;
 *     die Wörter aus der Stimme (`stimm_eindruck`, feste Liste).
 *   - der Tag in einer Zeile: `mood_days.einzeiler`, sonst der erste Satz
 *     der Tageszusammenfassung.
 *   - der Tag in Kürze: die Sätze des ersten Absatzes der
 *     Tageszusammenfassung („Mein Alltag“, abends erzeugt).
 *   - die Morgenmeditation dieses Tages (data/morgenmeditation).
 *
 * ⚠️ Hier stehen nur REINE Regeln — Server und Seite lesen dieselben. Was
 *    fehlt, bleibt leer (null / []), nie ein Platzhalter: Ein Tag ohne
 *    Aufnahme hat keine Stimmung, und eine erfundene wäre eine falsche
 *    Beobachtung.
 */
import { teileInSaetze } from "./satzgrenzen.js";
import { wienerStunde } from "./tagesgruss.js";

export type Tagesphase = "morgen" | "mittag" | "abend";

/** Was die Liste der Tage je Tag zusätzlich trägt. */
export interface ZeitleistenZeile {
  /** 1 … 5, oder null, wenn der Tag nicht bewertet wurde. */
  punkte: number | null;
  /** Ein bis zwei Wörter aus der Stimme, oder null. */
  wort: string | null;
  /** Der Tag in einer Zeile, oder null. */
  einzeiler: string | null;
  /** Länge der Morgenmeditation dieses Tages in Minuten, oder null. */
  meditationMin: number | null;
}

/** Was die Ansicht EINES Tages zusätzlich trägt. */
export interface Rueckblende extends ZeitleistenZeile {
  day: string;
  phasen: Partial<Record<Tagesphase, string>>;
  kurz: string[];
  /**
   * `anhoerbar`: liegt der Ton noch? Nach 14 Tagen wird er weggeräumt, der
   * Text bleibt (26.09.2026, Ticket 2560) — dann ohne „Anhören“.
   */
  meditation: { handlung: string | null; minuten: number | null; anhoerbar: boolean } | null;
}

export const TAG_MUSTER = /^\d{4}-\d{2}-\d{2}$/;

/** Ist das ein echter Kalendertag im Format YYYY-MM-DD? */
export function istTag(s: unknown): s is string {
  if (typeof s !== "string" || !TAG_MUSTER.test(s)) return false;
  const d = new Date(`${s}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/**
 * Valenz (-2 … +2) → gefüllte Punkte (1 … 5). Halbe Schritte runden auf.
 * Kein Wert → null, nicht „drei“: Mitte ist eine Aussage, keine Lücke.
 */
export function punkteAusValenz(valenz: number | null | undefined): number | null {
  if (typeof valenz !== "number" || !Number.isFinite(valenz)) return null;
  const v = Math.max(-2, Math.min(2, valenz));
  return Math.max(1, Math.min(5, Math.round(v + 3)));
}

/**
 * Die Tagesphase einer Wiener Stunde. Dieselben Grenzen wie das Angebot in
 * „Nebenbei“ (Ticket 2561): morgens bis 11, abends ab 17.
 */
export function phaseDerStunde(stunde: number): Tagesphase {
  if (stunde < 11) return "morgen";
  if (stunde < 17) return "mittag";
  return "abend";
}

/** Wörter nach Häufigkeit, bei Gleichstand das frühere zuerst. */
function haeufigste(woerter: readonly string[], anzahl: number): string[] {
  const zahl = new Map<string, number>();
  for (const w of woerter) zahl.set(w, (zahl.get(w) ?? 0) + 1);
  return [...zahl.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, anzahl)
    .map(([w]) => w);
}

/**
 * Die Stimm-Eindrücke EINES Tages → Wort des Tages und je Phase ein Wort.
 * Eindrücke ohne Stimmung (abgeschaltet, zu kurz, unklar) zählen nicht.
 */
export function stimmenDesTages(
  eindruecke: ReadonlyArray<{ ts: number; stimmung: string | null }>,
): { wort: string | null; phasen: Partial<Record<Tagesphase, string>> } {
  const mit = eindruecke
    .filter((e) => typeof e.stimmung === "string" && e.stimmung.trim())
    .sort((a, b) => a.ts - b.ts);
  const alle = mit.map((e) => (e.stimmung as string).trim());
  const wort = alle.length ? haeufigste(alle, 2).join(", ") : null;
  const phasen: Partial<Record<Tagesphase, string>> = {};
  for (const p of ["morgen", "mittag", "abend"] as const) {
    const w = mit
      .filter((e) => phaseDerStunde(wienerStunde(new Date(e.ts)) % 24) === p)
      .map((e) => (e.stimmung as string).trim());
    if (w.length) phasen[p] = haeufigste(w, 1)[0];
  }
  return { wort, phasen };
}

/**
 * Der erste Erzähl-Absatz der Tageszusammenfassung — ohne Überschrift,
 * Stimmungs-Satz, Listen und To-dos.
 */
function erzaehlAbsatz(markdown: string): string {
  const absaetze = (markdown || "")
    .split(/\r?\n\s*\r?\n/)
    .map((a) => a.trim())
    .filter(Boolean);
  for (const a of absaetze) {
    if (/^#{1,6}\s/.test(a) && !a.includes("\n")) continue;
    const ohneKopf = a.replace(/^#{1,6}\s.*(\r?\n|$)/, "").trim();
    if (!ohneKopf) continue;
    if (/^(stimmung|stimmungsverlauf)\s*:/i.test(ohneKopf)) continue;
    if (/^([-*+]|\d+\.)\s/.test(ohneKopf)) continue;
    if (/^\*\*[^*]+\*\*$/.test(ohneKopf)) continue;
    if (/^(offene\s+)?to-?dos?\b/i.test(ohneKopf)) continue;
    return ohneKopf.replace(/\s+/g, " ");
  }
  return "";
}

/** Höchstens so viele Punkte trägt „Der Tag in Kürze“. */
const KURZ_MAX = 4;

/**
 * „Der Tag in Kürze“: die Sätze des ersten Erzähl-Absatzes, höchstens vier.
 * Nichts wird umformuliert — es sind die Sätze der Zusammenfassung.
 */
export function kurzAusZusammenfassung(markdown: string | null | undefined): string[] {
  const absatz = erzaehlAbsatz(markdown ?? "");
  if (!absatz) return [];
  return teileInSaetze(absatz)
    .map((s) => s.trim())
    .filter((s) => s.length > 1)
    .slice(0, KURZ_MAX);
}

/** Ab so vielen Zeichen wird die eine Zeile gekürzt (Wortgrenze, „…“). */
const EINZEILER_MAX = 140;

/** Der Tag in einer Zeile: zuerst der Einzeiler der Bewertung, sonst Satz 1. */
export function einzeilerDesTages(
  einzeiler: string | null | undefined,
  zusammenfassung: string | null | undefined,
): string | null {
  const roh = (einzeiler ?? "").trim() || kurzAusZusammenfassung(zusammenfassung)[0] || "";
  if (!roh) return null;
  if (roh.length <= EINZEILER_MAX) return roh;
  const schnitt = roh.slice(0, EINZEILER_MAX);
  const leer = schnitt.lastIndexOf(" ");
  return `${(leer > 60 ? schnitt.slice(0, leer) : schnitt).replace(/[\s,;:–-]+$/, "")} …`;
}

/** Sekunden → ganze Minuten (mindestens 1), oder null. */
export function minutenAus(dauerSek: number | null | undefined): number | null {
  if (typeof dauerSek !== "number" || !Number.isFinite(dauerSek) || dauerSek <= 0) return null;
  return Math.max(1, Math.round(dauerSek / 60));
}

/* ------------------------------------------------------------------ *
 * Datum — deutsch (Österreich), unabhängig von der Sprache des Geräts
 * ------------------------------------------------------------------ */

const WOCHENTAGE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
/** Auch der Verlauf über die Monate (shared/tagesverlauf.ts) liest sie. */
export const MONATE = [
  "Jänner", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
export const MONATE_KURZ = [
  "Jän.", "Feb.", "März", "Apr.", "Mai", "Juni",
  "Juli", "Aug.", "Sept.", "Okt.", "Nov.", "Dez.",
];

function teile(day: string): { j: number; m: number; t: number; wt: number } | null {
  if (!istTag(day)) return null;
  const d = new Date(`${day}T12:00:00Z`);
  return { j: d.getUTCFullYear(), m: d.getUTCMonth(), t: d.getUTCDate(), wt: d.getUTCDay() };
}

/** Die Datumsspalte der Liste: „DO“ / „24“ / „Sept.“. */
export function datumsSpalte(day: string): { wochentag: string; zahl: string; monat: string } | null {
  const p = teile(day);
  if (!p) return null;
  return {
    wochentag: (WOCHENTAGE[p.wt] ?? "").slice(0, 2).toUpperCase(),
    zahl: String(p.t),
    monat: MONATE_KURZ[p.m] ?? "",
  };
}

/** Titel eines Tages: „Donnerstag, 24. September“ (mit Jahr, wenn es nicht das laufende ist). */
export function tagesTitel(day: string, heute = new Date()): string {
  const p = teile(day);
  if (!p) return day;
  const jahr = p.j !== heute.getFullYear() ? ` ${p.j}` : "";
  return `${WOCHENTAGE[p.wt]}, ${p.t}. ${MONATE[p.m]}${jahr}`;
}

/**
 * Ein Kalendertag mit Jahr: „26. Sept. 2026“ (`lang`) oder „26.09.2026“
 * (`zahlen`). Kein gültiger Tag → null. (27.09.2026, Ticket 2637 — das
 * „erlebt am“ der Ankermomente.)
 *
 * ⚠️ Über `teile()`, also als KALENDERTAG gelesen. `new Date("2026-09-26")`
 *    ist Mitternacht UTC; in Ortszeit westlich von UTC wird daraus der
 *    25. September.
 */
export function kalenderTag(day: string | null | undefined, form: "lang" | "zahlen"): string | null {
  const p = day ? teile(day.trim()) : null;
  if (!p) return null;
  if (form === "lang") return `${p.t}. ${MONATE_KURZ[p.m] ?? ""} ${p.j}`;
  const zwei = (n: number): string => String(n).padStart(2, "0");
  return `${zwei(p.t)}.${zwei(p.m + 1)}.${p.j}`;
}

/** Beschriftung für Vor/Zurück: „Mittwoch, 23.“. */
export function nachbarBeschriftung(day: string): string {
  const p = teile(day);
  return p ? `${WOCHENTAGE[p.wt]}, ${p.t}.` : day;
}

/**
 * Die Nachbarn eines Tages in der Reihe (neueste zuerst): `aelter` ist der
 * Tag davor, `neuer` der danach — beides nur, wenn es ihn gibt.
 */
export function nachbarTage(
  reihe: readonly string[],
  day: string,
): { aelter: string | null; neuer: string | null } {
  const i = reihe.indexOf(day);
  if (i < 0) return { aelter: null, neuer: null };
  return { aelter: reihe[i + 1] ?? null, neuer: i > 0 ? (reihe[i - 1] ?? null) : null };
}
